import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import {
  PopoverPanel as HeadlessPopoverPanel,
  type PopoverPanelProps as HeadlessPopoverPanelProps,
  Popover,
  PopoverButton,
} from '@headlessui/react';
import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 200;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface PopoverPanelProps extends PropsWithChildren {
  id?: string;
  testId?: string;
  className?: ClassName;
  panelClassName?: ClassName;
  /** Accessible name for both the trigger button and the panel. */
  label: string;
  /** Content rendered inside the trigger button. */
  trigger: ReactNode;
  placement?: NonNullable<Extract<HeadlessPopoverPanelProps['anchor'], string>>;
  /** Open on mouse hover in addition to click. Clicking pins the panel open. */
  openOnHover?: boolean;
}

export function PopoverPanel({
  id,
  testId,
  className,
  panelClassName,
  label,
  trigger,
  placement = 'bottom start',
  openOnHover = true,
  children,
}: PopoverPanelProps) {
  return (
    <Popover id={id} data-testid={testId} className={twx('inline-flex', className)}>
      {({ open, close }) => (
        <PopoverPanelBody
          open={open}
          close={close}
          label={label}
          trigger={trigger}
          placement={placement}
          openOnHover={openOnHover}
          panelClassName={panelClassName}
        >
          {children}
        </PopoverPanelBody>
      )}
    </Popover>
  );
}

interface PopoverPanelBodyProps extends PropsWithChildren {
  open: boolean;
  close: () => void;
  label: string;
  trigger: ReactNode;
  placement: NonNullable<PopoverPanelProps['placement']>;
  openOnHover: boolean;
  panelClassName?: ClassName;
}

const PopoverPanelBody = ({
  open,
  close,
  label,
  trigger,
  placement,
  openOnHover,
  panelClassName,
  children,
}: PopoverPanelBodyProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  const isPinnedRef = useRef(false);
  const isHoverClickRef = useRef(false);
  const isKeyboardActivationRef = useRef(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [focusPanelOnOpen, setFocusPanelOnOpen] = useState(false);

  openRef.current = open;

  const clearTimers = useCallback(() => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (open) {
      return;
    }
    isPinnedRef.current = false;
    setFocusPanelOnOpen(false);
    clearTimers();
  }, [open, clearTimers]);

  useEffect(() => {
    if (!(open && focusPanelOnOpen)) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      (panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? panel)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, focusPanelOnOpen]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      const activeElement = document.activeElement;
      if (buttonRef.current?.contains(activeElement) || panelRef.current?.contains(activeElement)) {
        buttonRef.current?.focus();
      }
      close();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open, close]);

  const handlePointerEnter = (event: React.PointerEvent) => {
    if (!openOnHover || event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(closeTimerRef.current);
    if (openRef.current) {
      return;
    }
    clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      if (openRef.current) {
        return;
      }
      isHoverClickRef.current = true;
      buttonRef.current?.click();
      isHoverClickRef.current = false;
    }, HOVER_OPEN_DELAY_MS);
  };

  const handlePointerLeave = (event: React.PointerEvent) => {
    if (!openOnHover || event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      if (openRef.current && !isPinnedRef.current) {
        close();
      }
    }, HOVER_CLOSE_DELAY_MS);
  };

  const handleButtonClick = (event: React.MouseEvent) => {
    if (isHoverClickRef.current) {
      isPinnedRef.current = false;
      return;
    }
    clearTimers();
    if (openRef.current && !isPinnedRef.current) {
      // Pin a hover-opened panel instead of letting the click close it.
      event.preventDefault();
      isPinnedRef.current = true;
      return;
    }
    if (openRef.current) {
      isPinnedRef.current = false;
      return;
    }
    isPinnedRef.current = true;
    setFocusPanelOnOpen(isKeyboardActivationRef.current);
  };

  return (
    <>
      <PopoverButton
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          // Route keyboard activation through the click handler so pinning behaves the same.
          event.preventDefault();
          isKeyboardActivationRef.current = true;
          buttonRef.current?.click();
          isKeyboardActivationRef.current = false;
        }}
        onKeyUp={(event) => {
          if (event.key === ' ') {
            event.preventDefault();
          }
        }}
        onClick={handleButtonClick}
        onPointerDown={() => {
          isKeyboardActivationRef.current = false;
        }}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={tw`rounded-full focus-visible:outline-2 focus-visible:outline-primary-main focus-visible:outline-offset-2`}
      >
        {trigger}
      </PopoverButton>
      <HeadlessPopoverPanel
        ref={panelRef}
        portal={true}
        anchor={{ to: placement, gap: 0, padding: 8 }}
        role="dialog"
        aria-label={label}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={tw`z-tooltip p-1`}
      >
        <div
          className={twx(
            'w-64 rounded border border-border-main bg-background-main p-2 font-normal text-content-main text-xs/4 shadow-md',
            panelClassName,
          )}
        >
          {children}
        </div>
      </HeadlessPopoverPanel>
    </>
  );
};
